import { Alert, AlertTitle, Skeleton } from "@mui/material";

import { useAuth } from "@/providers";
import { ViewAsOrgButton } from "@/shared/components";
import type { S3BucketIntegrationRecord } from "@/shared/domain/integrations";
import { useS3BucketBindings } from "@/shared/domain/integrations/hooks/useS3BucketBindings";
import { useOrg } from "@/shared/domain/orgs/hooks";

import styles from "./WrongOrgState.module.css";

function BindingRow({ binding }: { binding: S3BucketIntegrationRecord }) {
  const { data: org, isLoading, error } = useOrg(binding.org_id);

  return (
    <div className={styles.binding}>
      <div className={styles.bindingDetail}>
        <span className={styles.orgName}>{org?.name ?? binding.org_id}</span>
        <span className={styles.meta}>
          {binding.org_id} · {binding.aws_region} ·{" "}
          {binding.readonly ? "read only" : "read / write"}
        </span>
      </div>
      {isLoading && <Skeleton variant="rounded" className={styles.action} />}
      {org !== undefined && <ViewAsOrgButton org={org} size="small" />}
      {/* Usually means this account cannot see the org, which is worth saying rather than
          leaving a row that silently offers nothing. */}
      {error !== null && org === undefined && (
        <span className={styles.meta}>Not visible to this account</span>
      )}
    </div>
  );
}

interface WrongOrgStateProps {
  bucketName: string;
}

/**
 * Shown when the active org has no registration for this bucket.
 *
 * A bucket may be registered to several orgs, and an admin usually arrives from a link holding
 * only the bucket name, so the useful answer is which orgs can browse it — not a bare 404.
 */
export function WrongOrgState({ bucketName }: WrongOrgStateProps) {
  const { currentOrganization } = useAuth();
  const { data: bindings, isLoading, error } = useS3BucketBindings(bucketName);

  return (
    <Alert severity="warning" className={styles.panel}>
      <AlertTitle>
        {currentOrganization?.name ?? "This organization"} has no registration
        for {bucketName}
      </AlertTitle>
      <Bindings bindings={bindings} isLoading={isLoading} error={error} />
    </Alert>
  );
}

function Bindings({
  bindings,
  isLoading,
  error,
}: {
  bindings: S3BucketIntegrationRecord[] | undefined;
  isLoading: boolean;
  error: Error | null;
}) {
  if (isLoading) {
    return <Skeleton variant="rounded" className={styles.loading} />;
  }
  if (bindings === undefined) {
    return (
      <span>
        Could not load this bucket&apos;s registrations
        {error === null ? "." : `: ${error.message}`}
      </span>
    );
  }
  if (bindings.length === 0) {
    return <span>No organization has registered this bucket with Roboto.</span>;
  }
  return (
    <>
      <span>These organizations have registered it:</span>
      <div className={styles.bindings}>
        {bindings.map((binding) => (
          <BindingRow key={binding.org_id} binding={binding} />
        ))}
      </div>
    </>
  );
}
